import React from 'react';
import { cn } from '@/lib/core/utils';

/**
 * AnimatedGradientBackground
 *
 * Uses CSS radial-gradients instead of DOM blur orbs — zero GPU layers, zero compositing cost.
 * The subtle glow effect is achieved with layered radial-gradient() calls.
 */

export type AnimatedBackgroundVariant = 'default' | 'hero' | 'auth' | 'wizard';
export type AnimatedBackgroundOrbs = 'default' | 'minimal' | 'none';
export type WizardPhase = 'identity' | 'operations' | 'team' | 'launch';

interface AnimatedGradientBackgroundProps {
  variant?: AnimatedBackgroundVariant;
  orbs?: AnimatedBackgroundOrbs;
  className?: string;
  zIndex?: number;
  wizardPhase?: WizardPhase;
  wizardProgress?: number;
}

// ── Wizard phase configs — using radial-gradient strings instead of blur-orbs ─────

interface PhaseConfig {
  gradient: { light: string; dark: string };
  glowGradients: string;
}

const PHASE_CONFIGS: Record<WizardPhase, PhaseConfig> = {
  identity: {
    gradient: {
      light: 'linear-gradient(-45deg, hsl(271 41% 90%), hsl(271 41% 94%), hsl(230 40% 92%), hsl(271 50% 88%))',
      dark: 'linear-gradient(-45deg, hsl(271 41% 8%), hsl(271 41% 12%), hsl(230 30% 10%), hsl(271 50% 14%))',
    },
    glowGradients: 'radial-gradient(ellipse 20rem 20rem at 110% 20%, hsl(var(--primary) / 0.15) 0%, transparent 60%), radial-gradient(ellipse 14rem 14rem at 25% 110%, hsl(var(--secondary) / 0.10) 0%, transparent 60%)',
  },
  operations: {
    gradient: {
      light: 'linear-gradient(-45deg, hsl(230 40% 91%), hsl(240 38% 93%), hsl(255 35% 91%), hsl(220 42% 89%))',
      dark: 'linear-gradient(-45deg, hsl(230 35% 9%), hsl(240 30% 11%), hsl(255 28% 10%), hsl(220 35% 13%))',
    },
    glowGradients: 'radial-gradient(ellipse 20rem 20rem at 80% 10%, hsl(var(--primary) / 0.15) 0%, transparent 60%), radial-gradient(ellipse 18rem 18rem at 120% 105%, hsl(var(--secondary) / 0.10) 0%, transparent 60%)',
  },
  team: {
    gradient: {
      light: 'linear-gradient(-45deg, hsl(255 36% 90%), hsl(230 35% 92%), hsl(200 30% 91%), hsl(240 38% 89%))',
      dark: 'linear-gradient(-45deg, hsl(255 30% 9%), hsl(230 28% 11%), hsl(200 25% 10%), hsl(240 32% 13%))',
    },
    glowGradients: 'radial-gradient(ellipse 22rem 22rem at 95% 30%, hsl(var(--primary) / 0.15) 0%, transparent 60%), radial-gradient(ellipse 18rem 18rem at 20% 110%, hsl(var(--secondary) / 0.10) 0%, transparent 60%), radial-gradient(ellipse 12rem 12rem at 15% 5%, hsl(var(--grow) / 0.08) 0%, transparent 60%)',
  },
  launch: {
    gradient: {
      light: 'linear-gradient(-45deg, hsl(265 42% 89%), hsl(280 38% 91%), hsl(250 40% 90%), hsl(270 45% 87%))',
      dark: 'linear-gradient(-45deg, hsl(265 38% 9%), hsl(280 32% 12%), hsl(250 34% 10%), hsl(270 40% 14%))',
    },
    glowGradients: 'radial-gradient(ellipse 24rem 24rem at 85% 15%, hsl(var(--primary) / 0.15) 0%, transparent 60%), radial-gradient(ellipse 22rem 22rem at 20% 110%, hsl(var(--secondary) / 0.10) 0%, transparent 60%), radial-gradient(ellipse 16rem 16rem at 40% 40%, hsl(var(--grow) / 0.08) 0%, transparent 60%)',
  },
};

const NOISE_BG = `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`;
const NOISE_STYLE: React.CSSProperties = { backgroundImage: NOISE_BG, backgroundRepeat: 'repeat', backgroundSize: '512px 512px' };
const NOISE_CLASS = 'absolute inset-0 opacity-[0.15] dark:opacity-[0.05]';

// ── Radial-gradient glow strings by variant — zero GPU layers ──────────────

const GLOW_HERO = `
  radial-gradient(ellipse 40rem 40rem at 95% 0%, hsl(var(--primary) / 0.12) 0%, transparent 60%),
  radial-gradient(ellipse 30rem 30rem at 5% 100%, hsl(var(--secondary) / 0.09) 0%, transparent 60%),
  radial-gradient(ellipse 16rem 16rem at 38% 38%, hsl(142 71% 45% / 0.06) 0%, transparent 60%)
`;
const GLOW_AUTH = `
  radial-gradient(ellipse 20rem 20rem at 90% 25%, hsl(var(--primary) / 0.12) 0%, transparent 60%),
  radial-gradient(ellipse 16rem 16rem at 30% 95%, hsl(var(--secondary) / 0.09) 0%, transparent 60%)
`;
const GLOW_DEFAULT = `
  radial-gradient(ellipse 24rem 24rem at 90% 25%, hsl(var(--primary) / 0.12) 0%, transparent 60%),
  radial-gradient(ellipse 18rem 18rem at 30% 95%, hsl(var(--secondary) / 0.09) 0%, transparent 60%)
`;
const GLOW_MINIMAL = `
  radial-gradient(ellipse 16rem 16rem at 80% 30%, hsl(var(--primary) / 0.10) 0%, transparent 60%)
`;

function getGlow(variant: AnimatedBackgroundVariant, orbs: AnimatedBackgroundOrbs): string | undefined {
  if (orbs === 'none') return undefined;
  if (orbs === 'minimal') return GLOW_MINIMAL;
  if (variant === 'hero') return GLOW_HERO;
  if (variant === 'auth') return GLOW_AUTH;
  return GLOW_DEFAULT;
}

const AnimatedGradientBackground = React.forwardRef<
  HTMLDivElement,
  AnimatedGradientBackgroundProps
>(({ variant = 'default', orbs = 'default', className, zIndex = 0, wizardPhase = 'identity' }, ref) => {

  // ── Wizard variant ──────────────────────────────────────────────────
  if (variant === 'wizard') {
    const config = PHASE_CONFIGS[wizardPhase];

    return (
      <div
        ref={ref}
        className={cn('absolute inset-0 pointer-events-none overflow-hidden', className)}
        style={{ zIndex }}
      >
        {/* Gradient – light */}
        <div
          className="absolute inset-0 dark:hidden"
          style={{
            backgroundImage: config.gradient.light,
            backgroundSize: '400% 400%',
            animation: 'auth-gradient-shift 15s ease infinite',
            transition: 'background-image 800ms ease',
          }}
        />
        {/* Gradient – dark */}
        <div
          className="absolute inset-0 hidden dark:block"
          style={{
            backgroundImage: config.gradient.dark,
            backgroundSize: '400% 400%',
            animation: 'auth-gradient-shift 15s ease infinite',
            transition: 'background-image 800ms ease',
          }}
        />

        {/* Noise texture */}
        <div className={NOISE_CLASS} style={NOISE_STYLE} />

        {/* Glow — static CSS radial-gradient, zero GPU cost */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: config.glowGradients,
            transition: 'background 800ms ease',
          }}
        />
      </div>
    );
  }

  // ── Default / hero / auth variants ─────────────────────────────────
  const glow = getGlow(variant, orbs);

  return (
    <div
      ref={ref}
      className={cn('absolute inset-0 pointer-events-none overflow-hidden', className)}
      style={{ zIndex }}
    >
      {/* Animated gradient background */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: 'linear-gradient(-45deg, hsl(271 41% 86%), hsl(271 41% 90%), hsl(230 40% 88%), hsl(271 50% 84%))',
          backgroundSize: '400% 400%',
          animation: 'auth-gradient-shift 15s ease infinite',
        }}
      />
      <div
        className="absolute inset-0 dark:block hidden"
        style={{
          backgroundImage: 'linear-gradient(-45deg, hsl(271 41% 8%), hsl(271 41% 12%), hsl(230 30% 10%), hsl(271 50% 14%))',
          backgroundSize: '400% 400%',
          animation: 'auth-gradient-shift 15s ease infinite',
        }}
      />

      {/* Noise texture overlay */}
      <div className={NOISE_CLASS} style={NOISE_STYLE} />

      {/* Glow — static CSS radial-gradient, zero GPU cost */}
      {glow && (
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: glow }}
        />
      )}
    </div>
  );
});

AnimatedGradientBackground.displayName = 'AnimatedGradientBackground';

export default AnimatedGradientBackground;
